import { captureException } from '@/lib/sentry';
import type { UploadData } from '@/utils/cms-asset-upload';
import {
  CHUNK_INITIAL_RETRY_DELAY_MS,
  CHUNK_MAX_RETRIES,
  CHUNK_MAX_RETRY_DELAY_MS,
  CHUNK_SIZE_LARGE,
  CHUNK_SIZE_MEDIUM,
  CHUNK_SIZE_SMALL,
  FILE_SIZE_THRESHOLD_LARGE,
  FILE_SIZE_THRESHOLD_SMALL,
  KIB_256,
  MAX_CHUNK_SIZE,
  MIN_CHUNK_SIZE,
  TARGET_CHUNK_DURATION_SECONDS,
  TEST_CHUNK_SIZE,
} from '@/utils/constants/uploads';

import { BYTES_PER_MB } from '../constants/videos';

export type { UploadData };

/**
 * Returns the optimal chunk size for a given file size.
 * Larger files use larger chunks to reduce request overhead.
 * All returned sizes are multiples of 256 KiB (GCS requirement).
 * @internal Exported for testing only.
 * @example getChunkSizeForFile(500 * 1024 * 1024); // 16MB for 500MB file
 */
export function getChunkSizeForFile(fileSize: number): number {
  if (fileSize < FILE_SIZE_THRESHOLD_SMALL) {
    return CHUNK_SIZE_SMALL;
  }
  if (fileSize < FILE_SIZE_THRESHOLD_LARGE) {
    return CHUNK_SIZE_MEDIUM;
  }
  return CHUNK_SIZE_LARGE;
}

/**
 * Measures upload speed by uploading a test chunk.
 * @internal Exported for testing only.
 * @example const speedMbps = await measureUploadSpeed(sessionUrl, file);
 */
export async function measureUploadSpeed(sessionUrl: string, file: File): Promise<number> {
  const testChunk = file.slice(0, TEST_CHUNK_SIZE);
  const totalSize = file.size;

  const start = performance.now();

  const response = await fetch(sessionUrl, {
    body: testChunk,
    headers: {
      'Content-Length': String(testChunk.size),
      'Content-Range': `bytes 0-${testChunk.size - 1}/${totalSize}`,
    },
    method: 'PUT',
  });

  // Session expired - fatal, cannot continue
  if (response.status === 404 || response.status === 410) {
    throw new UploadError(
      'Upload session expired or not found.',
      false,
      UploadErrorType.SessionExpired,
      response.status
    );
  }

  // Accept 308 (more chunks needed) or 200/201 (complete for very small files)
  if (response.status !== 308 && response.status !== 200 && response.status !== 201) {
    throw new UploadError(
      `Speed test upload failed: ${response.status}`,
      isRetryableStatusCode(response.status),
      response.status >= 500 ? UploadErrorType.ServerError : UploadErrorType.ClientError,
      response.status
    );
  }

  const end = performance.now();
  const durationSeconds = (end - start) / 1000;
  const megabytes = testChunk.size / BYTES_PER_MB;
  const speedMbps = (megabytes * 8) / durationSeconds; // Convert MB/sec to Mbps

  return speedMbps;
}

/**
 * Calculates optimal chunk size based on measured upload speed.
 * Targets ~15 seconds per chunk for good progress feedback without too many requests.
 * Returns a size that is a multiple of 256 KiB (GCS requirement).
 * @internal Exported for testing only.
 * @example getChunkSizeForSpeed(50); // Returns chunk size for 50 Mbps connection
 */
export function getChunkSizeForSpeed(speedMbps: number): number {
  // Convert Mbps to bytes per second: Mbps / 8 * BYTES_PER_MB
  const bytesPerSecond = (speedMbps / 8) * BYTES_PER_MB;

  // Calculate chunk size for target duration
  let chunkSize = bytesPerSecond * TARGET_CHUNK_DURATION_SECONDS;

  // Clamp to min/max
  chunkSize = Math.max(MIN_CHUNK_SIZE, Math.min(MAX_CHUNK_SIZE, chunkSize));

  // Round down to nearest multiple of 256 KiB (GCS requirement)
  chunkSize = Math.floor(chunkSize / KIB_256) * KIB_256;

  return chunkSize;
}

/**
 * Calculates the retry delay using exponential backoff with jitter.
 * Jitter helps avoid thundering herd problems when multiple clients retry simultaneously.
 * Shared by the chunked-upload loop and the sermon publish/refresh retry loops.
 * @example calculateRetryDelay(0); // ~4000ms for first retry
 */
export function calculateRetryDelay(
  attempt: number,
  initialDelay: number = CHUNK_INITIAL_RETRY_DELAY_MS,
  maxDelay: number = CHUNK_MAX_RETRY_DELAY_MS
): number {
  const baseDelay = initialDelay * Math.pow(2, attempt);
  const cappedDelay = Math.min(baseDelay, maxDelay);
  // Add up to 20% random jitter to avoid thundering herd
  const jitter = cappedDelay * 0.2 * Math.random();
  return Math.floor(cappedDelay + jitter);
}

/**
 * Initiates a resumable upload session with Google Cloud Storage.
 *
 * @param uploadUrl - The presigned URL for the upload.
 * @param requestHeaders - Headers to include in the upload request, that come from the OptiMS batch_create request.
 * @returns The session URL for the resumable upload.
 * @throws Error if the initiation fails.
 *
 * @example
 * const sessionUrl = await initiateUpload(
 *   'https://storage.googleapis.com/...',
 *   {
 *     "x-goog-resumable": "start",
 *     "x-goog-meta-optims_workflow_id": "cms_guid_123e4567-e89b-12d3-a456-426614174000",
 *     "x-goog-if-generation-match": "0",
 *     "x-goog-content-length-range": "0,10737418240",
 *     "Content-Type": "video/png"
 *   }
 * );
 */
export async function initiateUpload(uploadUrl: string, requestHeaders: Record<string, string>): Promise<string> {
  const response = await fetch(uploadUrl, {
    headers: requestHeaders,
    method: 'POST',
  });

  if (response.status !== 201) {
    const text = await response.text();
    throw new Error(`Failed to initiate upload: ${response.status} - ${text}`);
  }

  // Ensure headers are fully read before returning to prevent race condition
  // with navigation aborting the connection before header access
  const location = response.headers.get('Location');

  // Force response body consumption to ensure connection is fully processed
  // This prevents ERR_ABORTED when navigation happens immediately after
  try {
    await response.text();
  } catch (error) {
    captureException(error, {
      tags: {
        function: 'initiateUpload',
        step: 'responseBodyConsumption',
      },
      extra: {
        uploadUrl,
        responseStatus: response.status,
        hasLocationHeader: Boolean(location),
      },
      level: 'warning',
    });
  }

  if (!location) {
    throw new Error('No Location header in upload response');
  }

  return location;
}

/**
 * Uploads a whole file in a single PUT to a V4 pre-signed URL (GCS "simple" upload).
 *
 * Unlike {@link uploadFile} (which speaks the GCS resumable-session protocol with Content-Range and
 * 308s), this is for slots whose `uploadType` is `simple` — e.g. a service issues a single signed
 * PUT URL for small images (thumbnails). The signed URL is bound to a specific `Content-Type`, so
 * `headers` MUST carry the same `Content-Type` the slot was signed with or GCS returns 403.
 *
 * @param uploadUrl - The V4 pre-signed PUT URL.
 * @param file - The file to upload in one request.
 * @param headers - Request headers; must include the signed `Content-Type`.
 * @throws UploadError if the PUT fails (retryable for 5xx/408/network).
 *
 * @example
 * await putSimpleUpload(slot.uploadUrl, thumbnailFile, { 'Content-Type': 'image/jpeg' });
 */
export async function putSimpleUpload(uploadUrl: string, file: File, headers: Record<string, string>): Promise<void> {
  let response: Response;
  try {
    response = await fetch(uploadUrl, { body: file, headers, method: 'PUT' });
  } catch (error) {
    throw new UploadError(
      `Network error during simple upload: ${error instanceof Error ? error.message : String(error)}`,
      true,
      UploadErrorType.NetworkError
    );
  }

  if (response.status === 200 || response.status === 201) {
    return;
  }
  const text = await response.text().catch(() => '');
  throw new UploadError(
    `Simple upload failed: ${response.status} - ${text}`,
    isRetryableStatusCode(response.status),
    response.status >= 500 ? UploadErrorType.ServerError : UploadErrorType.ClientError,
    response.status
  );
}

/**
 * Result of uploading a single chunk.
 */
export type ChunkUploadResult = {
  /** Whether this was the final chunk and upload is complete. */
  complete: boolean;
  /** The last byte that GCS has confirmed receiving. */
  lastPersistedByte: number;
};

/**
 * Options for chunked file upload.
 */
export type ChunkedUploadOptions = {
  /**
   * If true, measures upload speed with a test chunk and uses that to determine
   * optimal chunk size. Takes precedence over chunkSize if both are provided.
   * Only applies when startFromByte is 0 (fresh upload, not resume).
   */
  adaptiveChunkSize?: boolean;
  /** Chunk size in bytes. If not provided, will be calculated based on file size. */
  chunkSize?: number;
  /**
   * Callback invoked after each chunk is successfully uploaded.
   * @param lastUploadedByte - The 0-indexed position of the last byte uploaded.
   */
  onChunkComplete?: (lastUploadedByte: number) => void;
  /**
   * Callback to determine if upload should be stopped.
   * If returns true, upload will stop after current chunk.
   */
  shouldStop?: () => boolean;
  /** Starting byte offset for resuming uploads. Defaults to 0. */
  startFromByte?: number;
};

/**
 * Types of upload errors that can occur during the upload process.
 */
export enum UploadErrorType {
  /** Client error (4xx except 408). Not retryable. */
  ClientError = 'CLIENT_ERROR',
  /** Network connectivity issue (fetch failed). Retryable. */
  NetworkError = 'NETWORK_ERROR',
  /** All retry attempts exhausted. */
  RetriesExhausted = 'RETRIES_EXHAUSTED',
  /** Server error (5xx) or timeout (408). Retryable. */
  ServerError = 'SERVER_ERROR',
  /** Upload session has expired or was not found (404/410). Requires new session. */
  SessionExpired = 'SESSION_EXPIRED',
}

/**
 * Custom error class for upload failures that indicates whether retry is possible.
 */
export class UploadError extends Error {
  constructor(
    message: string,
    public readonly retryable: boolean,
    public readonly errorType: UploadErrorType,
    public readonly statusCode?: number
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

/**
 * Determines if an HTTP status code represents a retryable error.
 *
 * @param statusCode - The HTTP status code.
 * @returns True if the error is retryable.
 * @example
 * isRetryableStatusCode(503); // true (Service Unavailable)
 * isRetryableStatusCode(408); // true (Request Timeout)
 * isRetryableStatusCode(404); // false (Not Found)
 * isRetryableStatusCode(400); // false (Bad Request)
 */
function isRetryableStatusCode(statusCode: number): boolean {
  // 408 Request Timeout and 5xx server errors are retryable
  if (statusCode === 408) {
    return true;
  }
  if (statusCode >= 500 && statusCode < 600) {
    return true;
  }
  return false;
}

/**
 * Parses the Range header from a GCS 308 response to get the last persisted byte.
 * GCS returns Range header in format "bytes=0-1234" where 1234 is the last byte received.
 *
 * @param rangeHeader - The Range header value from the response.
 * @returns The last persisted byte (0-indexed), or -1 if no bytes have been persisted.
 * @example
 * parseRangeHeader('bytes=0-5242879'); // 5242879 (5MB uploaded)
 * parseRangeHeader(null);              // -1 (no bytes persisted)
 * parseRangeHeader('');                // -1 (invalid header)
 */
function parseRangeHeader(rangeHeader: string | null): number {
  if (!rangeHeader) {
    return -1;
  }
  // Format: "bytes=0-1234"
  const match = rangeHeader.match(/bytes=\d+-(\d+)/);
  if (match) {
    return parseInt(match[1], 10);
  }
  return -1;
}

/**
 * Queries GCS for the current upload status to determine how many bytes have been persisted.
 * This is used for resuming interrupted uploads.
 *
 * @param sessionUrl - The session URL from initiateUpload.
 * @param totalSize - The total file size in bytes.
 * @returns The last persisted byte offset (next upload should start from this + 1).
 * @throws UploadError if the session is expired or invalid.
 *
 * @example
 * const lastByte = await queryUploadStatus(sessionUrl, file.size);
 * // Resume upload from lastByte + 1
 */
export async function queryUploadStatus(sessionUrl: string, totalSize: number): Promise<number> {
  const response = await fetch(sessionUrl, {
    headers: {
      'Content-Length': '0',
      'Content-Range': `bytes */${totalSize}`,
    },
    method: 'PUT',
  });

  if (response.status === 308) {
    // Upload is incomplete, Range header tells us what's been received
    const rangeHeader = response.headers.get('Range');
    return parseRangeHeader(rangeHeader);
  }

  if (response.status === 200 || response.status === 201) {
    // Upload is already complete
    return totalSize - 1;
  }

  if (response.status === 404 || response.status === 410) {
    throw new UploadError(
      `Upload session expired or not found.`,
      false,
      UploadErrorType.SessionExpired,
      response.status
    );
  }

  const text = await response.text();
  throw new UploadError(`Failed to query upload status: ${text}`, false, UploadErrorType.ClientError, response.status);
}

/**
 * Result of querying GCS for resume offset.
 */
export type GetResumeStartByteResult = {
  /** Last byte GCS has persisted (0-based). Caller may sync local state when this exceeds lastUploadedByte. */
  lastPersistedByte: number;
  /** Next byte index to upload (0-based). */
  startByte: number;
};

/**
 * Queries GCS for persisted bytes and returns the byte offset to resume uploading from.
 * Use this before resuming an upload so the client does not re-upload data GCS already has.
 *
 * @param sessionUrl - Resumable upload session URL from initiateUpload.
 * @param totalSize - Total file size in bytes.
 * @param lastUploadedByte - Last byte the client believes was uploaded (0-based, -1 if none).
 * @returns Object with startByte (next byte to send) and lastPersistedByte (what GCS has).
 * @throws UploadError with SessionExpired (404/410) if the session is expired or not found.
 *
 * @example
 * const { startByte, lastPersistedByte } = await getResumeStartByte(sessionUrl, file.size, localLastByte);
 * if (lastPersistedByte > localLastByte) updateLocalState(lastPersistedByte);
 * await uploadFile(sessionUrl, file, { startFromByte: startByte });
 */
export async function getResumeStartByte(
  sessionUrl: string,
  totalSize: number,
  lastUploadedByte: number
): Promise<GetResumeStartByteResult> {
  // Fresh upload: nothing to resume, so skip the status probe. Against real GCS the probe would
  // just return 308/no-Range (start at 0); the GCS emulator (fake-gcs-server) instead treats the
  // empty-body `bytes */N` PUT as a finalize and answers 200, which would wrongly mark the upload
  // complete with a 0-byte object before any chunk is sent.
  if (lastUploadedByte < 0) {
    return { lastPersistedByte: -1, startByte: 0 };
  }
  const gcsPersistedByte = await queryUploadStatus(sessionUrl, totalSize);
  const lastPersistedByte = Math.max(gcsPersistedByte, lastUploadedByte);
  const startByte = lastPersistedByte + 1;
  return { startByte, lastPersistedByte };
}

/**
 * Uploads a single chunk to the GCS resumable upload session with automatic retry.
 * @internal Exported for testing only.
 * @example const result = await uploadChunk(sessionUrl, chunk, 0, 5242879, totalSize);
 */
export async function uploadChunk(
  sessionUrl: string,
  chunk: Blob,
  startByte: number,
  endByte: number,
  totalSize: number
): Promise<ChunkUploadResult> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= CHUNK_MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(sessionUrl, {
        body: chunk,
        headers: {
          'Content-Length': String(chunk.size),
          'Content-Range': `bytes ${startByte}-${endByte}/${totalSize}`,
        },
        method: 'PUT',
      });

      // Success - upload complete
      if (response.status === 200 || response.status === 201) {
        return { complete: true, lastPersistedByte: totalSize - 1 };
      }

      // 308 - chunk received, more to upload
      if (response.status === 308) {
        const rangeHeader = response.headers.get('Range');
        const lastPersistedByte = parseRangeHeader(rangeHeader);
        return { complete: false, lastPersistedByte };
      }

      // Session expired - non-retryable
      if (response.status === 404 || response.status === 410) {
        throw new UploadError(
          `Upload session expired or not found.`,
          false,
          UploadErrorType.SessionExpired,
          response.status
        );
      }

      // Check if retryable
      if (isRetryableStatusCode(response.status)) {
        const text = await response.text();
        lastError = new UploadError(`Chunk upload failed: ${text}`, true, UploadErrorType.ServerError, response.status);
      } else {
        // Non-retryable error
        const text = await response.text();
        throw new UploadError(`Chunk upload failed: ${text}`, false, UploadErrorType.ClientError, response.status);
      }
    } catch (error) {
      // Network errors are retryable
      if (error instanceof UploadError) {
        if (!error.retryable) {
          throw error;
        }
        lastError = error;
      } else {
        // Network error or other fetch failure - retryable
        lastError = new UploadError(
          `Network error during chunk upload: ${error instanceof Error ? error.message : String(error)}`,
          true,
          UploadErrorType.NetworkError
        );
      }
    }

    // If we have more retries, wait before trying again
    if (attempt < CHUNK_MAX_RETRIES) {
      const delay = calculateRetryDelay(attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // All retries exhausted
  throw lastError ?? new UploadError('Chunk upload failed after all retries', false, UploadErrorType.RetriesExhausted);
}

/**
 * Uploads a file to a GCS resumable upload session.
 * Supports resuming from a specific byte offset and provides progress callbacks.
 * When adaptiveChunkSize is enabled, measures upload speed first and optimizes chunk size.
 *
 * @param sessionUrl - The session URL from initiateUpload.
 * @param file - The File object to upload.
 * @param options - Optional configuration for chunk size, callbacks, and resume offset.
 * @throws UploadError if the upload fails after all retries.
 *
 * @example
 * await uploadFile(sessionUrl, file, {
 *   adaptiveChunkSize: true,
 *   onChunkComplete: (lastByte) => console.log(`Uploaded through byte ${lastByte}`),
 * });
 */
export async function uploadFile(sessionUrl: string, file: File, options: ChunkedUploadOptions = {}): Promise<void> {
  const { adaptiveChunkSize = false, chunkSize: providedChunkSize, onChunkComplete, startFromByte = 0 } = options;

  const totalSize = file.size;
  let currentByte = startFromByte;
  let chunkSize: number;

  // Determine chunk size - adaptive takes precedence for fresh uploads
  if (adaptiveChunkSize && startFromByte === 0) {
    try {
      // Measure speed with first 1MB chunk
      const speedMbps = await measureUploadSpeed(sessionUrl, file);
      chunkSize = getChunkSizeForSpeed(speedMbps);

      // The speed test already uploaded the first chunk, so continue from there
      currentByte = Math.min(TEST_CHUNK_SIZE, totalSize);

      // If the speed test completed the entire upload (small file), we're done
      if (currentByte >= totalSize) {
        onChunkComplete?.(totalSize - 1);
        return;
      }

      onChunkComplete?.(currentByte - 1);
    } catch (err) {
      // Fatal errors (session expired) should propagate - don't waste time falling back
      if (err instanceof UploadError && !err.retryable) {
        throw err;
      }
      // Speed test failed but error is retryable - capture for debugging
      captureException(err, {
        tags: {
          function: 'uploadFile',
          step: 'adaptiveChunkSizeSpeedTest',
        },
        extra: {
          fileSize: file.size,
          fileName: file.name,
          sessionUrl,
        },
        level: 'warning',
      });
      // Query GCS for actual persisted bytes instead of blindly resetting to 0
      try {
        const persistedByte = await queryUploadStatus(sessionUrl, totalSize);
        currentByte = persistedByte + 1;
      } catch (error) {
        // If query also fails, capture and reset to 0 as fallback
        captureException(error, {
          tags: {
            function: 'uploadFile',
            step: 'queryUploadStatusAfterSpeedTestFailure',
          },
          extra: {
            fileSize: file.size,
            fileName: file.name,
            sessionUrl,
            originalError: err instanceof Error ? err.message : String(err),
          },
          level: 'warning',
        });
        currentByte = 0;
      }
      chunkSize = providedChunkSize ?? getChunkSizeForFile(file.size);
    }
  } else {
    chunkSize = providedChunkSize ?? getChunkSizeForFile(file.size);
  }

  while (currentByte < totalSize) {
    if (options.shouldStop?.()) {
      return;
    }

    const endByte = Math.min(currentByte + chunkSize - 1, totalSize - 1);
    const chunk = file.slice(currentByte, endByte + 1);

    const result = await uploadChunk(sessionUrl, chunk, currentByte, endByte, totalSize);

    if (result.complete) {
      onChunkComplete?.(totalSize - 1);
      return;
    }

    // Move to the next byte after what GCS confirmed receiving
    currentByte = result.lastPersistedByte + 1;
    onChunkComplete?.(result.lastPersistedByte);
  }
}
